import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { useBootstrap } from '@/auth/bootstrap-context';
import { useAppState } from '@/store/context';
import { useSyncOptional } from '@/sync/sync-context';
import { importBudgetsToCloud } from '../import-api';
import style from './style.module.scss';
function budgetToDto(budget) {
    return {
        localBudgetId: budget.id,
        name: budget.name,
        currency: budget.currency,
        icon: budget.icon,
        weekStart: budget.weekStart,
        savingsEnabled: budget.savingsEnabled ?? false,
        showCurrentBalance: budget.showCurrentBalance ?? true,
        categories: budget.categories.map((c) => ({
            localCategoryId: c.id,
            type: c.type,
            label: c.label,
            icon: c.icon,
            sortOrder: 0,
            isSystem: false,
        })),
        transactions: budget.transactions.map((t) => ({
            localTransactionId: t.id,
            date: t.date,
            type: t.type,
            description: t.description,
            plannedAmount: t.planned,
            actualAmount: t.actual,
            fromSavings: t.fromSavings ?? false,
            categoryId: t.category ?? null,
            seriesId: t.seriesId ?? null,
            isStart: t.isStart ?? false,
        })),
    };
}
export default function ImportSection() {
    const bootstrap = useBootstrap();
    const { state, dispatch } = useAppState();
    const sync = useSyncOptional();
    const [selected, setSelected] = useState(new Set());
    const [phase, setPhase] = useState('idle');
    const [message, setMessage] = useState('');
    const [importedCount, setImportedCount] = useState(0);
    const candidates = useMemo(() => state.budgets.filter((b) => b.storageMode !== 'CloudBound'), [state.budgets]);
    if (bootstrap.data?.cloudSyncStatus?.cloudSyncEnabled !== true) {
        return null;
    }
    function toggleBudget(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return next;
        });
    }
    async function handleImport() {
        const budgetsToImport = candidates.filter((b) => selected.has(b.id));
        if (budgetsToImport.length === 0)
            return;
        setPhase('loading');
        setMessage('');
        try {
            const items = budgetsToImport.map(budgetToDto);
            const response = await importBudgetsToCloud(items);
            let count = 0;
            for (const result of response.results) {
                if (result.status === 'imported') {
                    if (result.cloudBudgetId !== result.localBudgetId) {
                        // Server assigned a new UUID — replace the local ID
                        dispatch({
                            type: 'REPLACE_BUDGET_ID',
                            oldId: result.localBudgetId,
                            newId: result.cloudBudgetId,
                            updates: { storageMode: 'CloudBound', version: result.version },
                        });
                    }
                    else {
                        dispatch({
                            type: 'UPDATE_BUDGET',
                            id: result.localBudgetId,
                            updates: { storageMode: 'CloudBound', version: result.version },
                        });
                    }
                    count++;
                }
            }
            setImportedCount(count);
            setSelected(new Set());
            setPhase('success');
            if (sync) {
                sync.triggerSync();
            }
        }
        catch (err) {
            const msg = err instanceof Error
                ? err.message
                : typeof err === 'object' && err !== null && 'errors' in err
                    ? (err.errors[0]?.message ?? 'Unknown error')
                    : 'Unknown error';
            setMessage(msg);
            setPhase('error');
        }
    }
    if (candidates.length === 0 && phase !== 'success') {
        return (_jsxs("div", { className: style['section'], children: [_jsx("div", { className: style['title'], children: "\u0418\u043C\u043F\u043E\u0440\u0442 \u0432 \u043E\u0431\u043B\u0430\u043A\u043E" }), _jsx("div", { className: style['all-synced'], children: "\u0412\u0441\u0435 \u0431\u044E\u0434\u0436\u0435\u0442\u044B \u0443\u0436\u0435 \u0432 \u043E\u0431\u043B\u0430\u043A\u0435" })] }));
    }
    if (candidates.length === 0 && phase === 'success') {
        return (_jsxs("div", { className: style['section'], children: [_jsx("div", { className: style['title'], children: "\u0418\u043C\u043F\u043E\u0440\u0442 \u0432 \u043E\u0431\u043B\u0430\u043A\u043E" }), _jsxs("div", { className: style['msg-success'], children: ["\u0418\u043C\u043F\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u043E ", importedCount, " \u0431\u044E\u0434\u0436\u0435\u0442", importedCount === 1 ? '' : importedCount < 5 ? 'а' : 'ов', " \u0432 \u043E\u0431\u043B\u0430\u043A\u043E"] })] }));
    }
    return (_jsxs("div", { className: style['section'], children: [_jsx("div", { className: style['title'], children: "\u0418\u043C\u043F\u043E\u0440\u0442 \u0432 \u043E\u0431\u043B\u0430\u043A\u043E" }), _jsx("div", { className: style['list'], children: candidates.map((b) => {
                    const checked = selected.has(b.id);
                    return (_jsxs("div", { className: `${style['item']}${checked ? ` ${style['item-selected']}` : ''}`, onClick: () => toggleBudget(b.id), children: [_jsx("div", { className: `${style['checkbox']}${checked ? ` ${style['checkbox-checked']}` : ''}`, children: checked ? '\u2713' : '' }), _jsx("div", { className: style['item-icon'], children: b.icon }), _jsxs("div", { className: style['item-info'], children: [_jsx("div", { className: style['item-name'], children: b.name }), _jsxs("div", { className: style['item-meta'], children: [b.currency, " \u00B7 ", b.transactions.length, " \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0439"] })] })] }, b.id));
                }) }), _jsxs("button", { className: style['btn-import'], disabled: selected.size === 0 || phase === 'loading', onClick: handleImport, children: [phase === 'loading' && _jsx("span", { className: style['spinner'] }), phase === 'loading' ? 'Импорт...' : `Импортировать в облако (${selected.size})`] }), phase === 'success' && (_jsxs("div", { className: style['msg-success'], children: ["\u0418\u043C\u043F\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u043E ", importedCount, " \u0431\u044E\u0434\u0436\u0435\u0442", importedCount === 1 ? '' : importedCount < 5 ? 'а' : 'ов', " \u0432 \u043E\u0431\u043B\u0430\u043A\u043E"] })), phase === 'error' && (_jsx("div", { className: style['msg-error'], children: message }))] }));
}
